import { Link, useParams } from '@tanstack/react-router';
import { ECOSYSTEM_LABELS } from '@repo/shared';
import type { AffectedPackage, CpeMatch, FixedInRange, SeverityLevel } from '@repo/shared';
import { ApiError } from '../lib/api';
import { useAdvisory } from '../features/advisories/hooks';
import { SourceLink } from '../components/SourceLink';
import { ExportJsonButton } from '../components/ExportJsonButton';
import { exportFilename } from '../lib/exportJson';
import styles from './AdvisoryPage.module.css';
import pkgStyles from './PackagePage.module.css';
import pageStyles from './pages.module.css';

const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  UNKNOWN: 'Unknown',
};

const SEVERITY_CLASS: Record<SeverityLevel, string> = {
  CRITICAL: pkgStyles.severityCritical ?? '',
  HIGH: pkgStyles.severityHigh ?? '',
  MEDIUM: pkgStyles.severityMedium ?? '',
  LOW: pkgStyles.severityLow ?? '',
  UNKNOWN: pkgStyles.severityUnknown ?? '',
};

/** Advisory page — `/advisory/$id` accepts CVE and GHSA ids. */
export function AdvisoryPage() {
  const { id } = useParams({ from: '/advisory/$id' });
  const advisory = useAdvisory(id);

  if (advisory.isPending) {
    return (
      <div className={pageStyles.page}>
        <p className={pageStyles.muted}>Loading advisory…</p>
      </div>
    );
  }

  if (advisory.isError) {
    const err = advisory.error;
    const is404 = err instanceof ApiError && err.status === 404;
    const is400 = err instanceof ApiError && err.status === 400;
    return (
      <div className={pageStyles.page}>
        {is404 || is400 ? (
          <div className={pkgStyles.notFound}>
            <h2 className={pkgStyles.notFoundTitle}>Advisory not found</h2>
            <p className={pageStyles.muted}>
              {is400 ? (
                <>
                  <strong>{id}</strong> doesn't look like a CVE (<code>CVE-YYYY-NNNN</code>) or GHSA
                  (<code>GHSA-xxxx-xxxx-xxxx</code>) id.
                </>
              ) : (
                <>
                  <strong>{id}</strong> is not known to OSV.dev. Check the id for typos.
                </>
              )}
            </p>
          </div>
        ) : (
          <p className={pageStyles.error}>Failed to load advisory: {err.message}</p>
        )}
      </div>
    );
  }

  const data = advisory.data;
  const cveId = cveIdFor(data.id, data.aliases);

  return (
    <div className={pageStyles.page}>
      <div className={styles.header}>
        <span
          className={`${pkgStyles.severityBadge} ${SEVERITY_CLASS[data.severity]}`}
          data-severity={data.severity}
        >
          {SEVERITY_LABELS[data.severity]}
        </span>
        <h1 className={styles.advisoryId}>{data.id}</h1>
        {data.cvssScore != null && (
          <span className={pageStyles.muted}>CVSS {data.cvssScore.toFixed(1)}</span>
        )}
        {data.cvssScore == null && data.nvdSeverity != null && (
          <span
            className={`${pkgStyles.severityBadge} ${SEVERITY_CLASS[data.nvdSeverity]}`}
            data-severity={data.nvdSeverity}
          >
            NVD: {SEVERITY_LABELS[data.nvdSeverity]}
          </span>
        )}
      </div>

      {data.aliases.length > 0 && (
        <div className={pkgStyles.aliases}>
          {data.aliases.map((alias) => (
            <span key={alias} className={pkgStyles.alias}>
              {alias}
            </span>
          ))}
        </div>
      )}

      {data.summary && <p className={styles.summary}>{data.summary}</p>}

      <div className={styles.metaRow}>
        {data.published && (
          <span className={pageStyles.muted}>
            Published {new Date(data.published).toLocaleDateString()}
          </span>
        )}
        {data.modified && (
          <span className={pageStyles.muted}>
            Updated {new Date(data.modified).toLocaleDateString()}
          </span>
        )}
        {data.vulnStatus && <span className={styles.statusTag}>NVD status: {data.vulnStatus}</span>}
        <SourceLink
          label="OSV.dev"
          href={`https://osv.dev/vulnerability/${encodeURIComponent(data.id)}`}
        />
        {data.hasNvdData && cveId && (
          <SourceLink
            label="NVD"
            href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cveId)}`}
          />
        )}
        <ExportJsonButton
          className={styles.exportButton}
          filename={exportFilename(data.id)}
          kind="advisory"
          data={data}
        />
      </div>

      <section className={pkgStyles.section}>
        <h2 className={pkgStyles.sectionTitle}>Affected packages ({data.affected.length})</h2>
        {data.affected.length > 0 ? (
          <ul className={styles.affectedList}>
            {data.affected.map((pkg, i) => (
              <AffectedRow key={`${pkg.osvEcosystem}:${pkg.name}:${i}`} pkg={pkg} />
            ))}
          </ul>
        ) : (
          <p className={pageStyles.muted}>No affected packages recorded.</p>
        )}
      </section>

      {data.weaknesses.length > 0 && (
        <section className={pkgStyles.section}>
          <h2 className={pkgStyles.sectionTitle}>Weaknesses (CWE)</h2>
          <div className={styles.cweList}>
            {data.weaknesses.map((cwe) => {
              const num = cwe.id.replace(/^CWE-/i, '');
              return (
                <a
                  key={cwe.id}
                  href={`https://cwe.mitre.org/data/definitions/${encodeURIComponent(num)}.html`}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.cweChip}
                >
                  {cwe.name ? `${cwe.id} — ${cwe.name}` : cwe.id}
                </a>
              );
            })}
          </div>
        </section>
      )}

      {data.cpeMatches.length > 0 && (
        <section className={pkgStyles.section}>
          <h2 className={pkgStyles.sectionTitle}>Affected configurations (NVD CPE)</h2>
          <ul className={styles.cpeList}>
            {data.cpeMatches.map((cpe, i) => (
              <li key={`${cpe.criteria}:${i}`} className={styles.cpeRow}>
                <code className={styles.cpeCriteria}>{cpe.criteria}</code>
                <span className={styles.cpeRange}>{formatCpeRange(cpe)}</span>
                {!cpe.vulnerable && <span className={styles.cpeNotVulnerable}>not vulnerable</span>}
              </li>
            ))}
          </ul>
          {data.cpeMatches.length === 100 && <p className={pageStyles.muted}>Showing first 100.</p>}
        </section>
      )}

      {data.references.length > 0 && (
        <section className={pkgStyles.section}>
          <h2 className={pkgStyles.sectionTitle}>References</h2>
          <ul className={styles.referenceList}>
            {data.references.map((ref) => (
              <li key={ref.url}>
                <a href={ref.url} target="_blank" rel="noreferrer" className={styles.referenceLink}>
                  {ref.url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Picks the CVE id for external NVD links: the advisory id itself when it is a
 * CVE, else the first CVE-shaped alias (handles GHSA-primary records).
 */
function cveIdFor(id: string, aliases: string[]): string | null {
  if (/^CVE-/i.test(id)) return id;
  return aliases.find((a) => /^CVE-/i.test(a)) ?? null;
}

/**
 * Composes a human version-range string from the four NVD CPE bounds, using
 * ≥/> for start bounds and ≤/< for end bounds. "all versions" when unbounded.
 */
function formatCpeRange(cpe: CpeMatch): string {
  const parts: string[] = [];
  if (cpe.versionStartIncluding) parts.push(`≥ ${cpe.versionStartIncluding}`);
  else if (cpe.versionStartExcluding) parts.push(`> ${cpe.versionStartExcluding}`);
  if (cpe.versionEndIncluding) parts.push(`≤ ${cpe.versionEndIncluding}`);
  else if (cpe.versionEndExcluding) parts.push(`< ${cpe.versionEndExcluding}`);
  return parts.length > 0 ? parts.join(', ') : 'all versions';
}

function formatRange(range: FixedInRange): string {
  const from = range.introduced && range.introduced !== '0' ? `≥ ${range.introduced}` : 'all';
  if (range.fixed) return `${from} → fixed in ${range.fixed}`;
  if (range.lastAffected) return `${from} → ≤ ${range.lastAffected} (no fix)`;
  return from;
}

function AffectedRow({ pkg }: { pkg: AffectedPackage }) {
  const ecosystemLabel = pkg.system ? ECOSYSTEM_LABELS[pkg.system] : pkg.osvEcosystem;

  return (
    <li className={styles.affectedRow}>
      <div className={styles.affectedHeader}>
        <span className={pkgStyles.ecosystemTag}>{ecosystemLabel}</span>
        {pkg.system ? (
          <Link
            to="/pkg/$system/$name"
            params={{ system: pkg.system, name: encodeURIComponent(pkg.name) }}
            search={{ v: undefined }}
            className={styles.affectedName}
          >
            {pkg.name}
          </Link>
        ) : (
          <span className={styles.affectedName}>{pkg.name}</span>
        )}
      </div>

      <ul className={styles.rangeList}>
        {pkg.ranges.map((range, i) => (
          <li key={i} className={styles.rangeItem}>
            {formatRange(range)}
          </li>
        ))}
      </ul>

      {pkg.fixedVersions.length > 0 && (
        <div className={pkgStyles.fixedIn}>
          <span className={pageStyles.muted}>Fixed in: </span>
          {pkg.fixedVersions.map((v) => (
            <span key={v} className={pkgStyles.fixedBadge}>
              {v}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}
